import { useContext } from "react";
import { GlobalDataContext } from "../contexts/GlobalDataContext";

/**
 * Hook to access global positions data
 * @returns {Object} { positions, loading, error, positionsUpdateTimestamp }
 */
export function useGlobalPositions() {
  const context = useContext(GlobalDataContext);

  if (!context) {
    throw new Error(
      "useGlobalPositions must be used within GlobalDataProvider"
    );
  }

  return {
    positions: context.positions,
    loading: context.loading.positions,
    error: context.errors.positions,
    positionsUpdateVersion: context.positionsUpdateVersion, // Expose version counter
  };
}

/**
 * Hook to access global orders data
 * @returns {Object} { orders, loading, error, refetch, ordersUpdateTimestamp }
 */
export function useGlobalOrders() {
  const context = useContext(GlobalDataContext);

  if (!context) {
    throw new Error("useGlobalOrders must be used within GlobalDataProvider");
  }

  return {
    orders: context.orders,
    loading: context.loading.orders,
    error: context.errors.orders,
    refetch: context.refetch.orders,
    ordersUpdateVersion: context.ordersUpdateVersion, // Expose version counter
  };
}


export function useGlobalRejectedOrders() {
  const context = useContext(GlobalDataContext);

  if (!context) {
    throw new Error("useGlobalOrders must be used within GlobalDataProvider");
  }

  return {
    rejectedOrders: context.rejectedOrders,
    loading: context.loading.rejectedOrders,
    error: context.errors.rejectedOrders,
    refetch: context.refetch.rejectedOrders,
    ordersUpdateVersion: context.ordersUpdateVersion, // Expose version counter
  };
}

/**
 * Hook to access global user mappings data
 * @returns {Object} { userMappings, loading, error, refetch }
 */
export function useGlobalUserMappings() {
  const context = useContext(GlobalDataContext);

  if (!context) {
    throw new Error(
      "useGlobalUserMappings must be used within GlobalDataProvider"
    );
  }

  return {
    userMappings: context.userMappings,
    loading: context.loading.userMappings,
    error: context.errors.userMappings,
    refetch: context.refetch.userMappings,
  };
}

/**
 * Hook to access global users data
 * @returns {Object} { users, loading, error, refetch }
 */
export function useGlobalUsers() {
  const context = useContext(GlobalDataContext);

  if (!context) {
    throw new Error("useGlobalUsers must be used within GlobalDataProvider");
  }

  return {
    users: context.users,
    loading: context.loading.users,
    error: context.errors.users,
    refetch: context.refetch.users,
  };
}

/**
 * Hook to access global index multipliers data
 * @returns {Object} { indexMultipliers, loading, error, refetch }
 */
export function useGlobalIndexMultipliers() {
  const context = useContext(GlobalDataContext);

  if (!context) {
    throw new Error(
      "useGlobalIndexMultipliers must be used within GlobalDataProvider"
    );
  }

  return {
    indexMultipliers: context.indexMultipliers,
    loading: context.loading.indexMultipliers,
    error: context.errors.indexMultipliers,
    refetch: context.refetch.indexMultipliers,
  };
}

/**
 * Hook to access all global data at once
 * Useful when you need multiple data sources
 * @returns {Object} All global data and states
 */
export function useGlobalData() {
  const context = useContext(GlobalDataContext);

  if (!context) {
    throw new Error("useGlobalData must be used within GlobalDataProvider");
  }

  return context;
}
